/**
 * Training Plan Setup — multi-step, sport-adaptive intake wizard.
 *
 * Purpose:
 *   Structured questionnaire that produces the training profile used by the
 *   plan generator. Replaces conversational plan-intake by the AI Coach so
 *   the athlete's daily AI-chat allowance is preserved for real coaching
 *   conversations. Every wizard call goes through `/api/athlete/training-
 *   intake` — never through `/api/ai-coach/*` — so quota isolation is
 *   enforced structurally.
 *
 * UX rules the wizard enforces:
 *   * one focused step at a time (Back/Continue),
 *   * sport-specific branching from Step 1 (cyclists never see running
 *     race-pace questions, runners never see FTP, etc.),
 *   * conditional Nutrition / Medical / Event / Strength sections,
 *   * long option lists are replaced by free-text fields with concrete
 *     placeholder examples ("Example: tempo, sweet spot, threshold, climbing"),
 *   * prefilled from existing athlete data with `Use this` / `Change` hints,
 *   * resumable — every step persists a draft to the backend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Bike,
  Check,
  CircleDot,
  Footprints,
  Loader2,
  Trophy,
  Users,
  Waves,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  commitTrainingIntake,
  fetchTrainingIntake,
  previewPlanStart,
  saveTrainingIntakeDraft,
} from "@/api/trainingIntakeApi";
import { fetchCurrentCoachingDecision } from "@/api/coachingDecisionApi";
import { generateTrainingPlan } from "@/api/trainingPlanApi";
import { formatDate } from "@/lib/coachingFormatters";
import {
  coachingDecisionBlocksPlanGeneration,
  coachingDecisionPrerequisiteMessage,
} from "@/lib/planGenerationPrerequisites";
import {
  PLAN_GENERATION_RESULT_KIND,
  interpretPlanGenerationResult,
} from "@/lib/interpretPlanGenerationResult";

// ---------------------------------------------------------------------------
// Sport catalogue + sport-specific field predicates
// ---------------------------------------------------------------------------

const SPORT_OPTIONS = [
  { id: "cycling", label: "Cycling", Icon: Bike },
  { id: "running", label: "Running", Icon: Footprints },
  { id: "swimming", label: "Swimming", Icon: Waves },
  { id: "walking", label: "Walking", Icon: Footprints },
  { id: "triathlon", label: "Triathlon", Icon: Trophy },
  { id: "duathlon", label: "Duathlon", Icon: Users },
  { id: "other", label: "Other", Icon: CircleDot },
];

const GOAL_EXAMPLES = {
  cycling: [
    { id: "improve_endurance", label: "Improve endurance" },
    { id: "improve_ftp", label: "Improve FTP" },
    { id: "improve_climbing", label: "Improve climbing" },
    { id: "prepare_event", label: "Prepare for an event / gran fondo" },
    { id: "improve_racing", label: "Improve racing performance" },
    { id: "general_fitness", label: "General fitness" },
  ],
  running: [
    { id: "improve_endurance", label: "Improve endurance" },
    { id: "improve_race_pace", label: "Improve race pace" },
    { id: "prepare_event", label: "Prepare for a race (5K / 10K / half / marathon)" },
    { id: "return_from_injury", label: "Return from injury" },
    { id: "general_fitness", label: "General fitness" },
  ],
  swimming: [
    { id: "improve_endurance", label: "Improve swim endurance" },
    { id: "improve_speed", label: "Improve swim speed" },
    { id: "prepare_event", label: "Prepare for an open-water event" },
    { id: "general_fitness", label: "General fitness" },
  ],
  walking: [
    { id: "general_fitness", label: "General fitness" },
    { id: "improve_endurance", label: "Improve endurance" },
    { id: "increase_distance_safely", label: "Increase distance safely" },
    { id: "complete_target_distance", label: "Complete a target distance" },
    { id: "prepare_for_event", label: "Prepare for a walking event / trek" },
    { id: "return_to_activity", label: "Return to activity" },
  ],
  triathlon: [
    { id: "prepare_event", label: "Prepare for a triathlon" },
    { id: "improve_endurance", label: "Improve endurance" },
    { id: "improve_racing", label: "Improve racing performance" },
    { id: "general_fitness", label: "General fitness" },
  ],
  duathlon: [
    { id: "prepare_event", label: "Prepare for a duathlon" },
    { id: "improve_endurance", label: "Improve endurance" },
    { id: "general_fitness", label: "General fitness" },
  ],
  other: [
    { id: "improve_endurance", label: "Improve endurance" },
    { id: "general_fitness", label: "General fitness" },
    { id: "prepare_event", label: "Prepare for an event" },
  ],
};

const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_LABEL = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

// Each readiness field is a 1–5 scale. To avoid ambiguity ("does high mean
// good or bad here?"), we surface an explicit direction label per field
// under the control. Positive-direction ("more is better") fields:
// sleep_quality, motivation, recovery_feeling. The remaining three are
// negative-direction ("more is worse").
const READINESS_FIELDS = [
  {
    key: "overall_fatigue",
    label: "Overall fatigue",
    lowLabel: "fresh",
    highLabel: "exhausted",
  },
  {
    key: "muscle_fatigue",
    label: "Muscle / leg fatigue",
    lowLabel: "loose",
    highLabel: "sore",
  },
  {
    key: "sleep_quality",
    label: "Sleep quality",
    lowLabel: "poor",
    highLabel: "excellent",
  },
  {
    key: "life_stress",
    label: "General life stress",
    lowLabel: "calm",
    highLabel: "very stressed",
  },
  {
    key: "motivation",
    label: "Motivation to train",
    lowLabel: "flat",
    highLabel: "eager",
  },
  {
    key: "recovery_feeling",
    label: "How recovered you feel",
    lowLabel: "not recovered",
    highLabel: "fully recovered",
  },
];

const isCyclingSport = (sport) => sport === "cycling" || sport === "triathlon" || sport === "duathlon";
const isRunningSport = (sport) => sport === "running" || sport === "triathlon" || sport === "duathlon";
const isSwimmingSport = (sport) => sport === "swimming" || sport === "triathlon";

const EVENT_GOAL_IDS = new Set(["prepare_event", "prepare_for_event"]);

const goalNeedsEvent = (goalId) => EVENT_GOAL_IDS.has(goalId);

// ---------------------------------------------------------------------------
// Initial intake shape (mirrors backend TrainingIntakeDocument)
// ---------------------------------------------------------------------------

const emptyEquipment = () => ({
  has_power_meter: null,
  has_heart_rate_monitor: null,
  has_smart_trainer: null,
  has_cadence_sensor: null,
  can_train_indoors: null,
  indoor_comfort_minutes: null,
  indoor_notes: null,
  has_gps_watch: null,
  has_treadmill: null,
  has_pool_access: null,
  has_open_water_access: null,
  environment_notes: null,
});

const emptyIntake = () => ({
  primary_sport: null,
  secondary_sports: [],
  goal: {
    goal_category: null,
    original_text: null,
    target_metric_value: null,
    target_metric_unit: null,
    target_date: null,
  },
  event: {
    preparing_for_event: false,
    event_name: null,
    event_date: null,
    target_distance_meters: null,
    target_finishing_time_seconds: null,
    event_notes: null,
  },
  current_fitness: [],
  availability: {
    days: WEEKDAYS.map((day) => ({
      weekday: day,
      available: false,
      max_minutes: null,
    })),
    preferred_long_session_day: null,
    preferred_rest_day: null,
    schedule_predictability: null,
    schedule_notes: null,
    // Athlete-controlled plan-start authority (see docs section 14c).
    // ``null`` means legacy row / unanswered — backend defaults to
    // START_TOMORROW at read time via
    // ``training_plan_start_authority.coerce_start_preference``.
    start_preference: null,
    custom_start_date: null,
    // Active-plan lifecycle answer (REPLACE_CURRENT_PLAN /
    // FINISH_CURRENT_PLAN_FIRST). ``null`` means "no conflict yet"
    // or "athlete hasn't picked" — the wizard renders the prompt when
    // the preview reports ``active_plan_conflict``.
    active_plan_conflict_choice: null,
  },
  readiness: {
    overall_fatigue: null,
    muscle_fatigue: null,
    sleep_quality: null,
    life_stress: null,
    motivation: null,
    recovery_feeling: null,
  },
  medical: {
    disclosure: "no",
    description: null,
    healthcare_restriction: false,
    healthcare_restriction_notes: null,
    // Structured Health & Safety Restrictions V1: explicit
    // checkbox-selected restriction codes with provenance. Free-text
    // above is context/notes only and is never parsed into codes.
    structured_restrictions: [],
  },
  equipment: emptyEquipment(),
  training_preferences: {
    enjoyed_workouts: null,
    disliked_workouts: null,
    hard_sessions_per_week: null,
    session_length_preference: null,
    // Soft target — exact minutes the athlete usually likes to train.
    // See backend ``TrainingPreferencesBlock.preferred_session_duration_minutes``.
    preferred_session_duration_minutes: null,
    additional_notes: null,
  },
  strength: {
    include_strength: null,
    strength_days_per_week: null,
    gym_access: null,
    strength_notes: null,
    other_sport_activity: false,
    other_sport_notes: null,
  },
  nutrition: {
    nutrition_opt_in: "no",
    primary_nutrition_goal: null,
    current_body_weight_kg: null,
    dietary_restrictions: null,
    food_allergies: null,
    long_session_fueling_habits: null,
    carbs_per_hour_grams: null,
    gut_issues_notes: null,
    hydration_notes: null,
    workout_specific_fueling_desired: null,
  },
});

const STEP_META = [
  {
    title: "Your Training",
    subtitle: "Sport background, equipment, and training access.",
  },
  {
    title: "Health & Safety",
    subtitle: "Injuries, limitations, and optional strength — only what you'd like respected.",
  },
  {
    title: "Preferences",
    subtitle: "How you like to train and optional nutrition.",
  },
  {
    title: "Review",
    subtitle: "Save your one-time athlete profile.",
  },
];
const STEP_TITLES = STEP_META.map((s) => s.title);
const TOTAL_STEPS = STEP_META.length;

/** Map legacy wizard positions onto the 4-step one-time setup flow. */
const migrateDraftStep = (oldStep) => {
  const n = Number(oldStep);
  if (!Number.isFinite(n) || n <= 0) return 1;
  // New 4-step drafts use steps 1–4 directly.
  if (n <= TOTAL_STEPS) return n;
  const map = { 5: 2, 6: 1, 7: 3, 8: 2, 9: 3, 10: 4 };
  return Math.min(map[n] || 1, TOTAL_STEPS);
};

// ---------------------------------------------------------------------------
// Tiny presentational primitives
// ---------------------------------------------------------------------------

const YesNoUnsure = ({ value, onChange, testId, options = null }) => {
  const opts = options || [
    { id: true, label: "Yes" },
    { id: false, label: "No" },
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {opts.map((opt) => (
        <button
          key={String(opt.id)}
          type="button"
          onClick={() => onChange(opt.id)}
          data-testid={`${testId}-option-${String(opt.id)}`}
          data-selected={value === opt.id ? "true" : "false"}
          className={`rounded-md border px-3 py-2 text-sm ${
            value === opt.id
              ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
              : "border-dark-border text-dark-text-primary"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const RatingScale = ({ value, onChange, testId }) => (
  <div className="flex gap-2" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        data-testid={`${testId}-rating-${n}`}
        aria-label={`Rating ${n} of 5`}
        className={`h-10 w-10 rounded-md border text-sm ${
          value === n
            ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
            : "border-dark-border text-dark-text-primary"
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

const FieldRow = ({ label, hint, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-dark-text-primary">
      {label}
    </label>
    {hint && <p className="text-xs text-dark-text-muted">{hint}</p>}
    {children}
  </div>
);

const SectionHeading = ({ title, hint }) => (
  <div className="space-y-1 border-t border-[var(--border-primary)] pt-4 first:border-t-0 first:pt-0">
    <h3 className="text-sm font-medium text-dark-text-primary">{title}</h3>
    {hint && <p className="text-xs text-dark-text-muted">{hint}</p>}
  </div>
);

const PrefillHint = ({ label }) =>
  label != null && label !== "" ? (
    <p className="text-xs text-brand-primary" data-testid="prefill-hint">
      Current value: {label}
    </p>
  ) : null;

const SaveStatus = ({ status, lastSavedAt }) => {
  if (status === "saving") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-dark-text-muted"
        data-testid="training-plan-setup-save-status"
        data-status="saving"
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        Saving…
      </span>
    );
  }
  if (status === "saved" && lastSavedAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-dark-text-muted"
        data-testid="training-plan-setup-save-status"
        data-status="saved"
      >
        <Check className="h-3 w-3 text-brand-primary" aria-hidden="true" />
        Draft saved
      </span>
    );
  }
  return null;
};

const Stepper = ({ currentStep, totalSteps, onJumpTo }) => {
  const meta = STEP_META[currentStep - 1] || {};
  return (
    <div className="w-full space-y-2" data-testid="training-intake-stepper">
      <div className="flex items-center justify-between text-xs text-dark-text-muted">
        <span>
          Step {currentStep} of {totalSteps}
        </span>
        <span className="font-medium text-dark-text-primary">{meta.title}</span>
      </div>
      {meta.subtitle && (
        <p
          className="text-xs text-dark-text-muted"
          data-testid="training-intake-step-subtitle"
        >
          {meta.subtitle}
        </p>
      )}
      {/* Progress bar */}
      <div className="h-1 rounded bg-dark-border">
        <div
          className="h-1 rounded bg-brand-primary transition-all"
          style={{ width: `${Math.round((currentStep / totalSteps) * 100)}%` }}
        />
      </div>
      {/* Scannable dot row — completed steps show a check, current is
          highlighted, upcoming are muted. On mobile the dots wrap. */}
      <div className="flex flex-wrap gap-1" data-testid="training-intake-step-dots">
        {STEP_META.map((step, idx) => {
          const n = idx + 1;
          const isCompleted = n < currentStep;
          const isCurrent = n === currentStep;
          const canJump = typeof onJumpTo === "function" && n <= currentStep;
          return (
            <button
              key={step.title}
              type="button"
              disabled={!canJump}
              onClick={canJump ? () => onJumpTo(n) : undefined}
              title={`Step ${n}: ${step.title}`}
              data-testid={`training-intake-step-dot-${n}`}
              data-state={
                isCurrent ? "current" : isCompleted ? "completed" : "upcoming"
              }
              className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full border text-[10px] font-semibold transition ${
                isCurrent
                  ? "border-brand-primary bg-brand-primary text-white"
                  : isCompleted
                    ? "border-brand-primary/60 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20"
                    : "border-dark-border text-dark-text-muted"
              } ${canJump ? "cursor-pointer" : "cursor-default"}`}
            >
              {isCompleted ? (
                <Check className="h-3 w-3" aria-hidden="true" />
              ) : (
                n
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const TrainingPlanSetup = ({
  open,
  onOpenChange,
  onGenerated,
  loadIntake = fetchTrainingIntake,
  saveDraft = saveTrainingIntakeDraft,
  commitIntake = commitTrainingIntake,
  runGenerate = generateTrainingPlan,
  loadCoachingDecision = fetchCurrentCoachingDecision,
  // When the backend short-circuits an ask-intent it emits
  // ``training_intake_mode`` = "new" or "review_existing". "review_existing"
  // forces the returning-athlete prompt regardless of local prefill state
  // so the athlete never falls back to conversational intake.
  forcedMode = null,
}) => {
  const [intake, setIntake] = useState(emptyIntake());
  const [prefilled, setPrefilled] = useState({});
  const [isReturningAthlete, setIsReturningAthlete] = useState(false);
  const [reviewedReturningPrompt, setReviewedReturningPrompt] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Draft persistence signals used by the footer's inline status label.
  // "idle" — nothing to report; "saving" — a persistDraft() is in-flight;
  // "saved" — the most recent persistDraft() succeeded; timestamp used
  // for the "Saved just now" hint. We reset to "idle" on close so the
  // footer doesn't flash stale status the next time the wizard opens.
  const [saveStatus, setSaveStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // When we open on step > 1 because the athlete had a saved draft, we
  // show a small "Welcome back" banner over the current step so they
  // aren't confused about why they don't start at Step 1. Dismissible.
  const [resumeBanner, setResumeBanner] = useState(null);
  const resumeBannerAckRef = useRef(false);
  // When a returning athlete jumps to a single section from the compact
  // prompt, we remember which section so the footer can show
  // "Back to profile summary" instead of the full stepper flow.
  const [returningEditingSection, setReturningEditingSection] = useState(null);

  // Merge helper — keeps the wizard's copy-on-write semantics simple.
  const patch = useCallback((section, partial) => {
    setIntake((prev) => ({
      ...prev,
      [section]:
        typeof partial === "function"
          ? partial(prev[section])
          : { ...(prev[section] || {}), ...(partial || {}) },
    }));
  }, []);

  useEffect(() => {
    if (!open) {
      // Reset transient UI-only state so a re-open picks the correct
      // returning-athlete entry point and does not inherit "editing
      // section" mode from a prior open.
      setReturningEditingSection(null);
      setReviewedReturningPrompt(false);
      setResumeBanner(null);
      resumeBannerAckRef.current = false;
      setSaveStatus("idle");
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await loadIntake();
        if (cancelled) return;
        if (result?.intake) {
          const defaults = emptyIntake();
          const merged = { ...defaults, ...result.intake };
          // Restore nested sub-blocks the server did not populate — but only
          // for the compound blocks (goal/event/availability/…). Scalar
          // fields like ``primary_sport`` and ``secondary_sports`` are kept
          // as returned.
          const nestedBlocks = [
            "goal",
            "event",
            "availability",
            "readiness",
            "medical",
            "equipment",
            "training_preferences",
            "strength",
            "nutrition",
          ];
          for (const key of nestedBlocks) {
            if (!merged[key] || typeof merged[key] !== "object") {
              merged[key] = defaults[key];
            } else {
              merged[key] = { ...defaults[key], ...merged[key] };
            }
          }
          if (!Array.isArray(merged.current_fitness)) {
            merged.current_fitness = [];
          }
          if (!Array.isArray(merged.availability?.days) || merged.availability.days.length === 0) {
            merged.availability = { ...merged.availability, days: defaults.availability.days };
          }
          setIntake(merged);
          setPrefilled(result.prefilled_from_chart1 || {});
          setIsReturningAthlete(Boolean(result.is_returning_athlete));
          if (result.intake?.draft_state?.current_step) {
            const savedStep = migrateDraftStep(
              result.intake.draft_state.current_step,
            );
            setStep(savedStep);
            if (savedStep > 1 && !resumeBannerAckRef.current) {
              setResumeBanner({
                atStep: savedStep,
                title: STEP_META[savedStep - 1]?.title || `Step ${savedStep}`,
              });
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.trainingIntakeError?.message || "Could not load your intake.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loadIntake]);

  const primarySport = intake.primary_sport;
  // Availability step guard: once the preview reports an active-plan
  // conflict, the wizard must not advance past step 3 until the athlete
  // has chosen Replace / Finish-current. See MASTER TRAINING PLAN SETUP /
  // DEADLOCK CLOSEOUT — "ACTIVE PLAN CONFLICT FOR NEW ATHLETE".
  const [availabilityConflictUnresolved, setAvailabilityConflictUnresolved] =
    useState(false);
  const availableDaysNeedMinutes = (intake.availability.days || []).filter(
    (d) => d.available,
  );
  const availableMinutesComplete = availableDaysNeedMinutes.every((d) => {
    const minutes = d.max_minutes;
    return Number.isInteger(minutes) && minutes >= 5 && minutes <= 600;
  });
  const canContinue = useMemo(() => {
    if (step === 1) return Boolean(primarySport);
    return true;
  }, [step, primarySport]);

  const persistDraft = useCallback(
    async (nextStep) => {
      setSaving(true);
      setSaveStatus("saving");
      setError(null);
      try {
        await saveDraft({
          primary_sport: intake.primary_sport || undefined,
          goal: intake.goal,
          event: intake.event,
          current_fitness: intake.current_fitness,
          availability: intake.availability,
          readiness: intake.readiness,
          medical: intake.medical,
          equipment: intake.equipment,
          training_preferences: intake.training_preferences,
          strength: intake.strength,
          nutrition: intake.nutrition,
          current_step: nextStep,
        });
        setSaveStatus("saved");
        setLastSavedAt(Date.now());
        return true;
      } catch (err) {
        setSaveStatus("idle");
        setError(err?.trainingIntakeError?.message || "Could not save progress.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [intake, saveDraft],
  );

  // Save-before-advance: previously ``setStep(next)`` ran before the
  // draft PUT so a 500/422 left the athlete on the next step with an
  // unsaved earlier step — Review then had nothing to commit and
  // Generate threw. We now persist first and only advance on success.
  // The current step stays put on failure and the error surfaces via
  // ``error`` state. See MASTER TRAINING PLAN RELIABILITY §5.
  const goNext = async () => {
    if (!canContinue || step >= TOTAL_STEPS) return;
    const next = step + 1;
    const ok = await persistDraft(next);
    if (ok) setStep(next);
  };

  const goBack = async () => {
    if (step <= 1) return;
    const next = step - 1;
    // Back-navigation must not silently discard the current step's
    // edits. Persist first; if it fails, stay put so the athlete can
    // retry or read the error.
    const ok = await persistDraft(next);
    if (ok) setStep(next);
  };

  const jumpToStep = async (target) => {
    const clamped = Math.min(Math.max(1, target), TOTAL_STEPS);
    const ok = await persistDraft(clamped);
    if (ok) setStep(clamped);
  };

  const saveSetupProfile = async () => {
    setSaving(true);
    setError(null);
    try {
      await commitIntake({
        primary_sport: intake.primary_sport,
        secondary_sports: intake.secondary_sports || [],
        goal: intake.goal?.goal_category ? intake.goal : { goal_category: null },
        event: intake.event,
        current_fitness: intake.current_fitness,
        availability: intake.availability,
        readiness: intake.readiness,
        medical: intake.medical,
        equipment: intake.equipment,
        training_preferences: intake.training_preferences,
        strength: intake.strength,
        nutrition: intake.nutrition,
      });
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.trainingIntakeError?.message || "Could not save your setup.");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // Returning-athlete prompt — replaces the wizard's first frame when the
  // athlete's profile is already set up. Anything unchanged skips straight
  // to a readiness refresh + generate.
  // ------------------------------------------------------------------
  const showReturningPrompt =
    (isReturningAthlete || forcedMode === "review_existing")
    && !reviewedReturningPrompt
    && step === 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="training-plan-setup"
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto border-[var(--border-primary)] bg-[var(--surface-primary)]"
      >
        <DialogHeader>
          <DialogTitle>Setup My Training Plan</DialogTitle>
          <DialogDescription>
            One-time athlete information — medical, equipment, and preferences.
            Use Quick Plan Update to change your goal, schedule, or plan.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-dark-text-muted" data-testid="training-plan-setup-loading">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Loading your training profile…
          </div>
        ) : showReturningPrompt ? (
          <ReturningAthletePrompt
            onNothingChanged={() => {
              setReviewedReturningPrompt(true);
              setStep(4);
              persistDraft(4);
            }}
            onReviewEverything={() => {
              setReviewedReturningPrompt(true);
              setStep(1);
            }}
            onUpdateSection={(target) => {
              setReviewedReturningPrompt(true);
              setReturningEditingSection(target);
              const map = {
                fitness: 1,
                health: 2,
                equipment: 1,
                preferences: 3,
                strength: 2,
                nutrition: 3,
              };
              const targetStep = map[target] || 1;
              setStep(targetStep);
              persistDraft(targetStep);
            }}
          />
        ) : (
          <>
            <Stepper
              currentStep={step}
              totalSteps={TOTAL_STEPS}
              onJumpTo={(target) => jumpToStep(target)}
            />
            {resumeBanner && (
              <div
                className="mt-3 flex items-start justify-between gap-3 rounded-md border border-brand-primary/40 bg-brand-primary/10 px-3 py-2 text-sm text-brand-primary"
                data-testid="training-plan-setup-resume-banner"
                role="status"
              >
                <span>
                  Welcome back — resuming from{" "}
                  <strong>
                    Step {resumeBanner.atStep}: {resumeBanner.title}
                  </strong>
                  .
                </span>
                <button
                  type="button"
                  className="text-xs underline underline-offset-2 hover:opacity-80"
                  data-testid="training-plan-setup-resume-banner-dismiss"
                  onClick={() => {
                    resumeBannerAckRef.current = true;
                    setResumeBanner(null);
                  }}
                >
                  Dismiss
                </button>
              </div>
            )}
            <div className="mt-4 space-y-4" data-testid={`training-plan-setup-step-${step}`}>
              {step === 1 && (
                <TrainingBackgroundStep
                  intake={intake}
                  patch={patch}
                  setIntake={setIntake}
                  prefilled={prefilled}
                />
              )}
              {step === 2 && (
                <HealthSafetyStep intake={intake} patch={patch} />
              )}
              {step === 3 && (
                <PreferencesOnlyStep intake={intake} patch={patch} />
              )}
              {step === 4 && (
                <ReviewStep intake={intake} jumpToStep={jumpToStep} profileOnly />
              )}
            </div>
          </>
        )}

        {error && (
          <div role="alert" className="mt-3 flex items-start gap-2 text-sm text-analytics-red" data-testid="training-plan-setup-error">
            <AlertCircle className="w-4 h-4 mt-0.5" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !showReturningPrompt && (
          <DialogFooter className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              {returningEditingSection ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    // Return to the compact returning-athlete prompt so
                    // the user does not get funnelled through unrelated
                    // steps. Preserves draft edits made in the section.
                    setReturningEditingSection(null);
                    setReviewedReturningPrompt(false);
                    setStep(1);
                  }}
                  data-testid="training-plan-setup-back-to-summary"
                >
                  Back to profile summary
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  disabled={step <= 1 || saving}
                  onClick={goBack}
                  data-testid="training-plan-setup-back"
                >
                  Back
                </Button>
              )}
              <SaveStatus
                status={saveStatus}
                lastSavedAt={lastSavedAt}
              />
            </div>
            <div className="flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center">
              {step < TOTAL_STEPS && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange?.(false)}
                  disabled={saving}
                  data-testid="training-plan-setup-save-close"
                  title="Your progress is already saved — you can resume any time."
                >
                  Save & close
                </Button>
              )}
              {step < TOTAL_STEPS ? (
                <Button
                  type="button"
                  onClick={goNext}
                  disabled={!canContinue || saving}
                  data-testid="training-plan-setup-continue"
                >
                  {saving ? "Saving…" : "Continue"}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={saveSetupProfile}
                  disabled={saving}
                  data-testid="training-plan-setup-generate"
                >
                  {saving ? "Saving…" : "Save Setup"}
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

const RETURNING_SECTION_OPTIONS = [
  { id: "fitness", label: "Update fitness" },
  { id: "preferences", label: "Update training preferences" },
  { id: "health", label: "Update health information" },
  { id: "equipment", label: "Update equipment" },
  { id: "strength", label: "Update strength / cross-training" },
  { id: "nutrition", label: "Update nutrition" },
];

const ReturningAthletePrompt = ({
  onNothingChanged,
  onReviewEverything,
  onUpdateSection,
}) => (
  <div className="space-y-4" data-testid="returning-athlete-prompt">
    <div>
      <h3 className="text-base font-semibold text-dark-text-primary">
        Your training profile is already set up
      </h3>
      <p className="mt-1 text-sm text-dark-text-muted">
        Tell us what changed before we build your next plan.
      </p>
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      <Button
        variant="outline"
        onClick={onNothingChanged}
        data-testid="returning-prompt-nothing"
        // ``whitespace-normal`` overrides the Button primitive's default
        // ``whitespace-nowrap`` so long labels wrap inside the button
        // container on narrow (320 px) viewports instead of horizontally
        // overflowing the modal. ``h-auto min-h-9`` restores the standard
        // control height as a minimum while allowing multi-line growth.
        className="sm:col-span-2 h-auto min-h-9 whitespace-normal py-2 text-center"
      >
        Nothing changed — review &amp; save
      </Button>
      {RETURNING_SECTION_OPTIONS.map((opt) => (
        <Button
          key={opt.id}
          variant="outline"
          onClick={() => onUpdateSection(opt.id)}
          data-testid={`returning-prompt-${opt.id}`}
          className="h-auto min-h-9 whitespace-normal py-2 text-center"
        >
          {opt.label}
        </Button>
      ))}
      <Button
        variant="ghost"
        onClick={onReviewEverything}
        data-testid="returning-prompt-review"
        className="sm:col-span-2 h-auto min-h-9 whitespace-normal py-2 text-center"
      >
        Review everything
      </Button>
    </div>
  </div>
);

const SportAndGoalStep = ({ intake, patch, setIntake, prefilled }) => {
  const primarySport = intake.primary_sport;
  const goalOptions = GOAL_EXAMPLES[primarySport] || [];
  const showEventDetails = goalNeedsEvent(intake.goal.goal_category);
  return (
    <div className="space-y-4" data-testid="step-sport-goal">
      <FieldRow
        label="What sport are you training for?"
        hint="Your coach will only ask questions relevant to this sport."
      >
        <div
          className="grid grid-cols-2 gap-2 sm:grid-cols-3"
          data-testid="step-sport-goal-sport"
        >
          {SPORT_OPTIONS.map((opt) => {
            const Icon = opt.Icon;
            const selected = primarySport === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`sport-option-${opt.id}`}
                data-selected={selected ? "true" : "false"}
                onClick={() =>
                  setIntake((prev) => ({
                    ...prev,
                    primary_sport: opt.id,
                    current_fitness: [{ sport: opt.id }],
                    // Sport switch must never leak a sport-specific
                    // structured target (e.g. cycling FTP W) into a
                    // different sport's authoritative goal.
                    goal: {
                      ...prev.goal,
                      goal_category: null,
                      target_metric_value: null,
                      target_metric_unit: null,
                    },
                    event: { ...prev.event, preparing_for_event: false },
                  }))
                }
                className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm transition ${
                  selected
                    ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                    : "border-dark-border text-dark-text-primary hover:border-brand-primary/60"
                }`}
              >
                {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </FieldRow>

      {primarySport && (
        <FieldRow label="What's your main goal?">
          <div className="flex flex-wrap gap-2" data-testid="step-sport-goal-category">
            {goalOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                data-testid={`goal-option-${opt.id}`}
                data-selected={intake.goal.goal_category === opt.id ? "true" : "false"}
                onClick={() => {
                  patch("goal", {
                    goal_category: opt.id,
                    // Sport-scoped: never leak a target value across sport
                    // switches. A newly-selected goal starts with no target
                    // captured; the athlete supplies the correct one below.
                    target_metric_value: null,
                    target_metric_unit: null,
                    target_date: null,
                  });
                  patch("event", {
                    preparing_for_event: goalNeedsEvent(opt.id),
                  });
                }}
                className={`rounded-md border px-3 py-2 text-sm text-left ${
                  intake.goal.goal_category === opt.id
                    ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                    : "border-dark-border text-dark-text-primary"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </FieldRow>
      )}

      {primarySport === "cycling" && intake.goal.goal_category === "improve_climbing" && (
        <>
          <FieldRow label="Target climb time (minutes)">
            <Input
              type="number"
              min="5"
              max="600"
              step="1"
              data-testid="goal-target-climb-time"
              value={
                Number.isFinite(intake.goal.target_metric_value)
                  ? intake.goal.target_metric_value
                  : ""
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  patch("goal", { target_metric_value: null, target_metric_unit: null });
                  return;
                }
                const parsed = Number.parseInt(raw, 10);
                if (Number.isFinite(parsed)) {
                  patch("goal", { target_metric_value: parsed, target_metric_unit: "min" });
                }
              }}
              placeholder="Example: 30"
            />
          </FieldRow>
          <FieldRow label="Target date">
            <Input
              type="date"
              data-testid="goal-target-date"
              value={intake.goal.target_date || ""}
              onChange={(e) => patch("goal", { target_date: e.target.value || null })}
            />
          </FieldRow>
        </>
      )}

      {primarySport === "cycling" && intake.goal.goal_category === "improve_ftp" && (
        <FieldRow label="Target FTP (W)">
          <Input
            type="number"
            min="50"
            max="700"
            step="1"
            data-testid="goal-target-ftp"
            value={
              Number.isFinite(intake.goal.target_metric_value)
                ? intake.goal.target_metric_value
                : ""
            }
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                patch("goal", { target_metric_value: null, target_metric_unit: null });
                return;
              }
              const parsed = Number.parseInt(raw, 10);
              if (Number.isFinite(parsed)) {
                patch("goal", { target_metric_value: parsed, target_metric_unit: "W" });
              }
            }}
            placeholder="Example: 250"
            aria-describedby="goal-target-ftp-help"
          />
          <p id="goal-target-ftp-help" className="text-xs text-dark-text-muted mt-1">
            Watts. Your target FTP is required so plan generation can prescribe power.
          </p>
        </FieldRow>
      )}

      {showEventDetails && (
        <div className="space-y-3 rounded-md border border-[var(--border-primary)] bg-[var(--surface-secondary)] p-3" data-testid="event-details">
          <p className="text-sm text-dark-text-primary">Event details</p>
          <FieldRow label="Event name">
            <Input
              data-testid="event-name"
              value={intake.event.event_name || ""}
              onChange={(e) => patch("event", { event_name: e.target.value || null })}
              placeholder="Example: City half marathon"
            />
          </FieldRow>
          <FieldRow label="Event date">
            <Input
              type="date"
              data-testid="event-date"
              value={intake.event.event_date || ""}
              onChange={(e) => patch("event", { event_date: e.target.value || null })}
            />
          </FieldRow>
          <FieldRow label="Target distance (km, optional)">
            <Input
              type="number"
              min="0"
              data-testid="event-distance"
              value={
                intake.event.target_distance_meters
                  ? intake.event.target_distance_meters / 1000
                  : ""
              }
              onChange={(e) =>
                patch("event", {
                  target_distance_meters: e.target.value
                    ? Number(e.target.value) * 1000
                    : null,
                })
              }
            />
          </FieldRow>
          <FieldRow
            label="Notes about the event (optional)"
            hint="Terrain, conditions, or anything your coach should know."
          >
            <Textarea
              data-testid="event-notes"
              value={intake.event.event_notes || ""}
              onChange={(e) => patch("event", { event_notes: e.target.value || null })}
              rows={2}
              placeholder="Example: hilly course, aid stations every 10 km."
            />
          </FieldRow>
        </div>
      )}

      <FieldRow
        label="Anything else about your goal? (optional)"
        hint="A sentence in your own words helps your coach understand context."
      >
        <Textarea
          data-testid="goal-original-text"
          value={intake.goal.original_text || ""}
          onChange={(e) => patch("goal", { original_text: e.target.value || null })}
          rows={2}
          placeholder={
            primarySport === "walking"
              ? "Example: build up to regular 5 km walks without knee pain."
              : "Example: I want to hold 200 W on a 4-hour ride without cramping."
          }
        />
      </FieldRow>
      {prefilled?.experience_level && (
        <PrefillHint label={`Experience level from your profile: ${prefilled.experience_level}`} />
      )}
    </div>
  );
};

const TrainingBackgroundStep = ({ intake, patch, setIntake, prefilled }) => (
  <div className="space-y-5" data-testid="step-training-background">
    <FieldRow
      label="What sport are you training for?"
      hint="Used to show the right equipment and fitness questions."
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="setup-primary-sport">
        {SPORT_OPTIONS.map((opt) => {
          const Icon = opt.Icon;
          const selected = intake.primary_sport === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              data-testid={`sport-option-${opt.id}`}
              data-selected={selected ? "true" : "false"}
              onClick={() =>
                setIntake((prev) => ({
                  ...prev,
                  primary_sport: opt.id,
                  current_fitness: [{ sport: opt.id }],
                }))
              }
              className={`rounded-md border px-3 py-2 text-sm ${
                selected
                  ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                  : "border-dark-border text-dark-text-primary"
              }`}
            >
              {Icon ? <Icon className="mr-1 inline h-4 w-4" aria-hidden="true" /> : null}
              {opt.label}
            </button>
          );
        })}
      </div>
    </FieldRow>
    <SectionHeading
      title="Sport background"
      hint="Experience level helps set a safe starting point — performance metrics come from your activities and goals elsewhere."
    />
    <CurrentFitnessStep
      intake={intake}
      patch={patch}
      setIntake={setIntake}
      prefilled={prefilled}
    />
    <SectionHeading
      title="Equipment & environment"
      hint="Only shown for your sport — skip anything that doesn't apply."
    />
    <EquipmentStep intake={intake} patch={patch} />
  </div>
);

const HealthSafetyStep = ({ intake, patch }) => (
  <div className="space-y-5" data-testid="step-health-safety">
    <MedicalStep intake={intake} patch={patch} />
    <SectionHeading
      title="Strength / cross-training"
      hint="Optional — say no if you don't want strength in your plan."
    />
    <StrengthStep intake={intake} patch={patch} />
  </div>
);

const PreferencesOnlyStep = ({ intake, patch }) => (
  <div className="space-y-5" data-testid="step-preferences">
    <SectionHeading
      title="Training style"
      hint="Short notes are enough — no need to list every workout type."
    />
    <PreferencesStep intake={intake} patch={patch} />
    <SectionHeading
      title="Nutrition"
      hint="Only answer if you want fueling guidance with your plan."
    />
    <NutritionStep intake={intake} patch={patch} />
  </div>
);

const PreferencesAndReadinessStep = ({ intake, patch }) => (
  <div className="space-y-5" data-testid="step-preferences-readiness">
    <SectionHeading
      title="Training style"
      hint="Short notes are enough — no need to list every workout type."
    />
    <PreferencesStep intake={intake} patch={patch} />
    <SectionHeading
      title="Nutrition"
      hint="Only answer if you want fueling guidance with your plan."
    />
    <NutritionStep intake={intake} patch={patch} />
    <SectionHeading
      title="How you feel right now"
      hint="Optional 1–5 check-in. You can refresh this later without redoing setup."
    />
    <ReadinessStep intake={intake} patch={patch} />
  </div>
);

const CurrentFitnessStep = ({ intake, patch, setIntake, prefilled }) => {
  const sport = intake.primary_sport;
  const cf =
    (intake.current_fitness || []).find((row) => row.sport === sport) || {
      sport,
    };
  const updateCf = (partial) => {
    setIntake((prev) => {
      const others = (prev.current_fitness || []).filter((row) => row.sport !== sport);
      return {
        ...prev,
        current_fitness: [
          ...others,
          { ...cf, sport, ...partial },
        ],
      };
    });
  };

  return (
    <div className="space-y-4" data-testid="step-current-fitness">
      {/*
        Experience level is sport-scoped and drives Stage 5 / Stage 6
        declared-baseline authority for a first-plan athlete with no
        observed activity data. Backend field: current_fitness[i].experience_level
        (see athlete_training_intake_models.SportCurrentFitness) which the
        commit fan-out writes to athlete_sport_profiles.experience_level.
        We use the canonical AthleteSportProfile vocabulary — beginner /
        intermediate / advanced — without inventing new values.
      */}
      <FieldRow
        label="Your experience with this sport"
        hint="Helps set a safe starting workload — pick the closest fit."
      >
        <select
          className="app-control text-sm w-full"
          data-testid="sport-experience-level"
          value={cf.experience_level || ""}
          onChange={(e) =>
            updateCf({ experience_level: e.target.value || null })
          }
        >
          <option value="">Choose your experience level</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </FieldRow>
      <PrefillHint
        label={prefilled.experience_level
          ? `Profile experience: ${prefilled.experience_level}`
          : null}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Athlete-controlled plan-start question. The backend is the sole authority
// on start dates (see ATHLETE-CONTROLLED TRAINING PLAN START V1); this
// component only *displays* the deterministic preview returned by
// ``POST /api/athlete/training-intake/plan-start-preview``. There is
// deliberately no local date arithmetic here — that eliminates the risk of
// the wizard preview drifting from what the plan generator resolves.
// ---------------------------------------------------------------------------
// Displayed in the same order as the product spec (MASTER TRAINING PLAN
// SETUP / DEPLOYMENT CLOSEOUT, Part 9). ``START_NEXT_WEEK`` is the
// dedicated "clean-break, start next Mon-Sun week" answer — distinct
// from ``NEXT_AVAILABLE`` which is simply the first available weekday
// strictly after today.
const PLAN_START_OPTIONS = [
  { id: "START_TODAY", label: "Start today", hint: "immediately" },
  { id: "START_TOMORROW", label: "Start tomorrow", hint: "recommended" },
  { id: "NEXT_AVAILABLE", label: "Next available training day", hint: null },
  { id: "START_NEXT_WEEK", label: "Start next week", hint: null },
  { id: "CUSTOM_DATE", label: "Choose a date", hint: null },
];

// Closed vocabulary — see backend ``ActivePlanConflictChoice`` enum in
// ``training_plan_start_authority``. Prose is deliberately unsupported.
const ACTIVE_PLAN_CONFLICT_CHOICES = [
  {
    id: "REPLACE_CURRENT_PLAN",
    label:
      "Replace my current plan and start the new plan on the date I selected",
  },
  {
    id: "FINISH_CURRENT_PLAN_FIRST",
    label: "Finish my current plan first, then start the new plan",
  },
];

const PlanStartQuestion = ({
  intake,
  patch,
  previewFn = previewPlanStart,
  onConflictStateChange,
}) => {
  const preference = intake.availability.start_preference || "START_TOMORROW";
  const customDate = intake.availability.custom_start_date || "";
  const conflictChoice =
    intake.availability.active_plan_conflict_choice || null;
  const anyAvailable = (intake.availability.days || []).some((d) => d.available);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    // Wait until the athlete has selected at least one training day
    // AND (for CUSTOM_DATE) a date. Otherwise the backend returns 422
    // / null and the preview area shows a gentle hint.
    if (!anyAvailable) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    if (preference === "CUSTOM_DATE" && !customDate) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    previewFn({
      startPreference: preference,
      customStartDate: preference === "CUSTOM_DATE" ? customDate : null,
      activePlanConflictChoice: conflictChoice,
    })
      .then((result) => {
        if (cancelled) return;
        setPreview(result);
        setPreviewError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setPreviewError(
          err?.trainingIntakeError?.message
          || "Could not preview your plan start date.",
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [anyAvailable, preference, customDate, conflictChoice, previewFn]);

  // Report unresolved conflict state up to the wizard so it can block
  // advancing past the Weekly Availability step until the athlete picks
  // Replace / Finish-current (MASTER TRAINING PLAN SETUP / DEADLOCK
  // CLOSEOUT — "ACTIVE PLAN CONFLICT FOR NEW ATHLETE").
  useEffect(() => {
    if (!onConflictStateChange) return undefined;
    const unresolved = Boolean(
      preview && preview.active_plan_conflict && !conflictChoice,
    );
    onConflictStateChange(unresolved);
    // On unmount clear the flag so leaving the availability step doesn't
    // leave the wizard permanently stuck.
    return () => onConflictStateChange(false);
  }, [preview, conflictChoice, onConflictStateChange]);

  return (
    <div
      className="space-y-3 rounded-md border border-dark-border p-3"
      data-testid="plan-start-question"
    >
      <p className="text-sm text-dark-text-primary">
        When would you like your training plan to start?
      </p>
      <div className="flex flex-col gap-1.5">
        {PLAN_START_OPTIONS.map((opt) => (
          <label
            key={opt.id}
            className="flex items-center gap-2 text-sm text-dark-text-primary"
            data-testid={`plan-start-option-${opt.id}`}
          >
            <input
              type="radio"
              name="plan-start-preference"
              value={opt.id}
              checked={preference === opt.id}
              onChange={() => patch("availability", {
                start_preference: opt.id,
                // Clear custom date if the athlete leaves CUSTOM_DATE.
                custom_start_date: opt.id === "CUSTOM_DATE"
                  ? intake.availability.custom_start_date
                  : null,
                // A different start preference may resolve a conflict
                // (or reveal a new one) — clear the athlete's prior
                // choice so the wizard re-asks against the new
                // requested date. Backend is still the sole authority.
                active_plan_conflict_choice: null,
              })}
            />
            <span>{opt.label}</span>
            {opt.hint && (
              <span className="text-xs text-dark-text-muted">— {opt.hint}</span>
            )}
          </label>
        ))}
      </div>
      {preference === "CUSTOM_DATE" && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-dark-text-muted">Start date</span>
          <Input
            type="date"
            data-testid="plan-start-custom-date"
            value={customDate}
            onChange={(e) =>
              patch("availability", {
                custom_start_date: e.target.value || null,
              })
            }
          />
        </div>
      )}
      <div
        className="text-xs text-dark-text-muted"
        data-testid="plan-start-preview"
      >
        {!anyAvailable && (
          <span>Select at least one training day above to preview your first
            training day.
          </span>
        )}
        {anyAvailable && preference === "CUSTOM_DATE" && !customDate && (
          <span>Pick a date to preview your first training day.</span>
        )}
        {previewLoading && anyAvailable && (
          <span>Calculating your first training day…</span>
        )}
        {previewError && (
          <span data-testid="plan-start-preview-error">{previewError}</span>
        )}
        {preview && !previewLoading && !previewError && (
          <PlanStartPreviewSummary preview={preview} />
        )}
      </div>
      {preview
        && preview.active_plan_conflict
        && !conflictChoice
        && (
          <ActivePlanConflictPrompt
            preview={preview}
            onChoose={(choice) =>
              patch("availability", {
                active_plan_conflict_choice: choice,
              })}
          />
        )}
      {preview && conflictChoice && (
        <ActivePlanConflictAcknowledged
          preview={preview}
          choice={conflictChoice}
          onReset={() =>
            patch("availability", { active_plan_conflict_choice: null })}
        />
      )}
    </div>
  );
};

const ActivePlanConflictPrompt = ({ preview, onChoose }) => (
  <div
    className="rounded-md border border-analytics-amber/60 bg-analytics-amber/10 p-3 text-sm text-dark-text-primary"
    data-testid="active-plan-conflict-prompt"
    role="alert"
  >
    <p className="mb-2 font-medium">
      You already have a training plan running until{" "}
      <strong>
        {formatDate(preview.active_block_end_date, preview.timezone)}
      </strong>
      . What would you like to do?
    </p>
    <div className="flex flex-col gap-1.5">
      {ACTIVE_PLAN_CONFLICT_CHOICES.map((opt) => (
        <label
          key={opt.id}
          className="flex items-start gap-2"
          data-testid={`active-plan-conflict-option-${opt.id}`}
        >
          <input
            type="radio"
            name="active-plan-conflict-choice"
            value={opt.id}
            onChange={() => onChoose(opt.id)}
            className="mt-0.5"
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
    <p className="mt-2 text-xs text-dark-text-muted">
      Cancel this wizard step to keep your current plan unchanged.
    </p>
  </div>
);

const ActivePlanConflictAcknowledged = ({ preview, choice, onReset }) => (
  <div
    className="rounded-md border border-dark-border p-3 text-xs text-dark-text-muted"
    data-testid="active-plan-conflict-acknowledged"
  >
    {choice === "REPLACE_CURRENT_PLAN" ? (
      <span>
        Your current plan (until{" "}
        {formatDate(preview.active_block_end_date, preview.timezone)}) will be
        replaced. New plan begins{" "}
        <strong>
          {formatDate(preview.first_session_date, preview.timezone)}
        </strong>
        .
      </span>
    ) : (
      <span>
        Your current plan will finish on{" "}
        {formatDate(preview.active_block_end_date, preview.timezone)}. New plan
        begins{" "}
        <strong>
          {formatDate(preview.first_session_date, preview.timezone)}
        </strong>
        .
      </span>
    )}
    <button
      type="button"
      onClick={onReset}
      className="ml-2 underline"
      data-testid="active-plan-conflict-reset"
    >
      Change
    </button>
  </div>
);

const _WEEKDAY_LABEL = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu",
  friday: "Fri", saturday: "Sat", sunday: "Sun",
};

const PlanStartPreviewSummary = ({ preview }) => {
  // Every date is strict YYYY-MM-DD; render via the shared formatter
  // which is timezone-neutral (see PERMANENT TRAINING DAY / CALENDAR
  // DATE AUTHORITY FIX).
  const firstLabel = formatDate(preview.first_session_date, preview.timezone);
  const differs = preview.effective_start_date !== preview.requested_start_date;
  const deferred = preview.deferred_by_active_plan;
  return (
    <span>
      Expected first training day: <strong>{firstLabel}</strong>
      <br />
      <span
        className="text-xs text-dark-text-muted"
        data-testid="plan-start-timezone"
      >
        Plan dates use your training timezone: <strong>{preview.timezone}</strong>.
        Today in your timezone is {formatDate(preview.athlete_today, preview.timezone)}.
      </span>
      <br />
      {deferred && (
        <>
          {" "}— your current plan runs until{" "}
          {formatDate(preview.requested_start_date, preview.timezone)}, so this
          plan will begin{" "}
          {formatDate(preview.effective_start_date, preview.timezone)}.
        </>
      )}
      {differs && !deferred && (
        <>
          {" "}(you asked for{" "}
          {formatDate(preview.requested_start_date, preview.timezone)}; the
          first eligible training weekday is{" "}
          {formatDate(preview.first_session_date, preview.timezone)}).
        </>
      )}
    </span>
  );
};

const AvailabilityStep = ({ intake, patch, onConflictStateChange }) => {
  const availableDays = (intake.availability.days || []).filter((d) => d.available);
  return (
  <div className="space-y-4" data-testid="step-availability">
    {availableDays.length > 0 && (
      <p className="meta-text rounded-md border border-brand-primary/30 bg-brand-primary/5 px-3 py-2 text-sm text-dark-text-primary" data-testid="availability-days-summary">
        Your coach will plan around <strong>{availableDays.length}</strong> training
        day{availableDays.length === 1 ? "" : "s"} per week
        {availableDays.every((d) => d.max_minutes)
          ? ` (${availableDays.map((d) => DAY_LABEL[d.weekday]).join(", ")})`
          : ""}.
      </p>
    )}
    <FieldRow
      label="Which days can you train?"
      hint="Tap each day, then enter how many minutes you have. Required for available days."
    >
      {WEEKDAYS.map((day) => {
        const entry = intake.availability.days.find((d) => d.weekday === day) || {
          weekday: day,
          available: false,
          max_minutes: null,
        };
        const updateDay = (partial) => {
          patch("availability", (prev) => ({
            ...prev,
            days: WEEKDAYS.map((weekday) => {
              const current =
                prev.days.find((d) => d.weekday === weekday) || {
                  weekday,
                  available: false,
                  max_minutes: null,
                };
              if (weekday !== day) return current;
              return { ...current, ...partial };
            }),
          }));
        };
        return (
          <div key={day} className="flex flex-wrap items-center gap-3" data-testid={`availability-day-${day}`}>
            <span className="w-12 text-sm text-dark-text-primary">
              {DAY_LABEL[day]}
            </span>
            <YesNoUnsure
              value={entry.available}
              onChange={(v) =>
                updateDay({
                  available: v,
                  // Don't auto-force a duration when the athlete toggles
                  // a day available. Existing values are preserved; new
                  // toggles start with ``null`` so the numeric input
                  // stays empty until the athlete types their preferred
                  // duration. See MASTER TRAINING PLAN RELIABILITY §13.
                  max_minutes: v ? entry.max_minutes ?? null : null,
                })
              }
              testId={`availability-day-${day}-yesno`}
            />
            {entry.available && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-dark-text-muted">
                    How many minutes can you train?
                  </span>
                  <Input
                    type="number"
                    min="5"
                    max="600"
                    step="1"
                    required
                    className="w-24"
                    placeholder="e.g. 75"
                    aria-label={`How many minutes can you train on ${DAY_LABEL[day]}?`}
                    data-testid={`availability-day-${day}-minutes`}
                    value={entry.max_minutes ?? ""}
                    onChange={(e) =>
                      updateDay({
                        max_minutes: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  />
                </div>
                <span className="text-[11px] text-dark-text-muted">
                  Required. Enter 5–600 minutes exactly. There is no default.
                </span>
              </div>
            )}
          </div>
        );
      })}
    </FieldRow>
    <FieldRow label="Preferred long-session day (optional)">
      <Input
        data-testid="availability-long-day"
        value={intake.availability.preferred_long_session_day || ""}
        onChange={(e) =>
          patch("availability", { preferred_long_session_day: e.target.value || null })
        }
      />
    </FieldRow>
    <FieldRow label="Preferred rest day (optional)">
      <Input
        data-testid="availability-rest-day"
        value={intake.availability.preferred_rest_day || ""}
        onChange={(e) => patch("availability", { preferred_rest_day: e.target.value || null })}
      />
    </FieldRow>
    <FieldRow label="Is your schedule fixed or flexible?">
      <YesNoUnsure
        value={intake.availability.schedule_predictability}
        onChange={(v) => patch("availability", { schedule_predictability: v })}
        testId="availability-predictability"
        options={[
          { id: "fixed", label: "Fixed" },
          { id: "flexible", label: "Flexible" },
        ]}
      />
    </FieldRow>
    <FieldRow label="Anything else about your weekly schedule? (optional)">
      <Textarea
        data-testid="availability-notes"
        value={intake.availability.schedule_notes || ""}
        onChange={(e) => patch("availability", { schedule_notes: e.target.value || null })}
        rows={2}
        placeholder="Example: Work is unpredictable on Wednesdays and Thursdays."
      />
    </FieldRow>
    <PlanStartQuestion
      intake={intake}
      patch={patch}
      onConflictStateChange={onConflictStateChange}
    />
  </div>
  );
};

const ReadinessStep = ({ intake, patch }) => (
  <div className="space-y-4" data-testid="step-readiness">
    <p className="text-xs text-dark-text-muted">
      Rate each item from 1 (very low) to 5 (very high). You can refresh these
      later without redoing the whole questionnaire.
    </p>
    {READINESS_FIELDS.map(({ key, label, lowLabel, highLabel }) => (
      <FieldRow key={key} label={label}>
        <RatingScale
          value={intake.readiness[key]}
          onChange={(v) => patch("readiness", { [key]: v })}
          testId={`readiness-${key}`}
        />
        <p
          className="mt-1 flex justify-between text-[11px] text-dark-text-muted"
          data-testid={`readiness-${key}-hint`}
        >
          <span>1 = {lowLabel}</span>
          <span>5 = {highLabel}</span>
        </p>
      </FieldRow>
    ))}
  </div>
);

// V1 structured restriction taxonomy — kept in-sync with the backend
// enum ``StructuredRestrictionCode`` in
// ``backend/athlete_health_structured_safety.py``. Labels are athlete-
// friendly (no diagnosis language); the wizard never invents codes
// from prose.
const STRUCTURED_RESTRICTION_OPTIONS = [
  { code: "NO_RUNNING", label: "Running" },
  { code: "NO_CYCLING", label: "Cycling" },
  { code: "NO_SWIMMING", label: "Swimming" },
  { code: "NO_HIGH_INTENSITY", label: "High-intensity training" },
  { code: "NO_THRESHOLD", label: "Threshold efforts" },
  { code: "NO_VO2", label: "VO₂ max efforts" },
  { code: "NO_ANAEROBIC", label: "Sprint / anaerobic efforts" },
  { code: "NO_LONG_SESSIONS", label: "Long sessions" },
  { code: "TRAINING_SUSPENDED", label: "All training until cleared" },
];

const StructuredRestrictionsPicker = ({ intake, patch }) => {
  const rows = intake.medical.structured_restrictions || [];
  const hasCode = (code) => rows.some((r) => r.code === code);
  const findRow = (code) => rows.find((r) => r.code === code);
  const setRows = (next) => patch("medical", { structured_restrictions: next });

  const toggle = (code, source) => {
    if (hasCode(code)) {
      setRows(rows.filter((r) => r.code !== code));
      // If MAX_SESSION_DURATION was tied to NO_LONG_SESSIONS, no
      // action needed — it's a separate row.
    } else {
      setRows([...rows, { code, source: source || "athlete_reported" }]);
    }
  };

  const maxDurRow = findRow("MAX_SESSION_DURATION");
  const setMaxDuration = (minutes) => {
    const next = rows.filter((r) => r.code !== "MAX_SESSION_DURATION");
    if (minutes !== null && minutes !== undefined && minutes !== "") {
      const parsed = Number.parseInt(minutes, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        next.push({
          code: "MAX_SESSION_DURATION",
          source: maxDurRow?.source || "athlete_reported",
          value_minutes: parsed,
        });
      }
    }
    setRows(next);
  };

  return (
    <div className="space-y-3" data-testid="medical-structured-picker">
      <p className="text-sm text-dark-text-muted">
        Tick anything a healthcare professional (or you) have decided you should
        avoid right now. This is not a diagnosis — it just tells Endurava which
        workouts to keep out of your plan.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {STRUCTURED_RESTRICTION_OPTIONS.map((opt) => (
          <label
            key={opt.code}
            data-testid={`medical-structured-option-${opt.code}`}
            className="flex items-start gap-2 rounded-md border border-dark-border bg-dark-elev p-2"
          >
            <input
              type="checkbox"
              checked={hasCode(opt.code)}
              onChange={() => toggle(opt.code)}
              className="mt-1"
              data-testid={`medical-structured-checkbox-${opt.code}`}
            />
            <span className="text-sm text-dark-text-primary">{opt.label}</span>
          </label>
        ))}
      </div>
      {hasCode("NO_LONG_SESSIONS") && (
        <div className="space-y-1" data-testid="medical-structured-max-duration">
          <label className="text-sm text-dark-text-primary">
            Maximum single-session duration (optional, minutes)
          </label>
          <input
            type="number"
            min="15"
            max="600"
            inputMode="numeric"
            value={maxDurRow?.value_minutes ?? ""}
            placeholder="e.g. 60"
            data-testid="medical-structured-max-duration-input"
            onChange={(e) => setMaxDuration(e.target.value)}
            className="w-full rounded-md border border-dark-border bg-dark-elev p-2 text-sm text-dark-text-primary"
          />
          <p className="text-xs text-dark-text-muted">
            If left blank, we cap long sessions at 90 minutes.
          </p>
        </div>
      )}
    </div>
  );
};

const MedicalStep = ({ intake, patch }) => {
  const hasStructured = (intake.medical.structured_restrictions || []).length > 0;
  const [showRestrictions, setShowRestrictions] = useState(hasStructured);

  return (
  <div className="space-y-4" data-testid="step-medical">
    <FieldRow
      label="Do you currently have any injury, medical condition, or physical limitation that may affect training?"
    >
      <YesNoUnsure
        value={intake.medical.disclosure}
        onChange={(v) => patch("medical", { disclosure: v })}
        testId="medical-disclosure"
        options={[
          { id: "no", label: "No" },
          { id: "yes", label: "Yes" },
          { id: "prefer_not_to_say", label: "Prefer not to say" },
        ]}
      />
    </FieldRow>
    {intake.medical.disclosure === "yes" && (
      <div className="space-y-3" data-testid="medical-details">
        <FieldRow
          label="Tell your coach what we should know"
          hint="Examples: knee pain, back issue, recent surgery, asthma, physician restriction."
        >
          <Textarea
            data-testid="medical-description"
            value={intake.medical.description || ""}
            onChange={(e) => patch("medical", { description: e.target.value || null })}
            rows={3}
            placeholder="Describe anything that should shape your training plan."
          />
        </FieldRow>
        <FieldRow
          label="Have you received a diagnosis or medical advice relevant to your training?"
        >
          <YesNoUnsure
            value={intake.medical.healthcare_restriction}
            onChange={(v) => patch("medical", { healthcare_restriction: v })}
            testId="medical-healthcare-restriction"
          />
        </FieldRow>
        {intake.medical.healthcare_restriction && (
          <FieldRow label="Please describe it briefly">
            <Textarea
              data-testid="medical-healthcare-notes"
              value={intake.medical.healthcare_restriction_notes || ""}
              onChange={(e) =>
                patch("medical", { healthcare_restriction_notes: e.target.value || null })
              }
              rows={2}
              placeholder="Example: cardiologist advised moderate intensity only."
            />
          </FieldRow>
        )}
        <div className="space-y-2">
          <button
            type="button"
            className="text-sm text-brand-primary underline underline-offset-2"
            data-testid="medical-toggle-restrictions"
            onClick={() => setShowRestrictions((v) => !v)}
          >
            {showRestrictions ? "Hide specific training limits" : "Add specific training limits (optional)"}
          </button>
          {showRestrictions && (
            <StructuredRestrictionsPicker intake={intake} patch={patch} />
          )}
        </div>
      </div>
    )}
  </div>
  );
};

const EquipmentStep = ({ intake, patch }) => {
  const sport = intake.primary_sport;
  return (
    <div className="space-y-4" data-testid="step-equipment">
      {isCyclingSport(sport) && (
        <div className="space-y-3" data-testid="equipment-cycling-block">
          <FieldRow label="Do you train with a power meter?">
            <YesNoUnsure
              value={intake.equipment.has_power_meter}
              onChange={(v) => patch("equipment", { has_power_meter: v })}
              testId="equipment-power-meter"
            />
          </FieldRow>
          <FieldRow label="Do you use a heart-rate monitor?">
            <YesNoUnsure
              value={intake.equipment.has_heart_rate_monitor}
              onChange={(v) => patch("equipment", { has_heart_rate_monitor: v })}
              testId="equipment-hr"
            />
          </FieldRow>
          <FieldRow label="Do you have a smart trainer / indoor trainer?">
            <YesNoUnsure
              value={intake.equipment.has_smart_trainer}
              onChange={(v) => patch("equipment", { has_smart_trainer: v })}
              testId="equipment-smart-trainer"
            />
          </FieldRow>
          <FieldRow label="Can you train indoors?">
            <YesNoUnsure
              value={intake.equipment.can_train_indoors}
              onChange={(v) => patch("equipment", { can_train_indoors: v })}
              testId="equipment-indoor"
            />
          </FieldRow>
          {intake.equipment.can_train_indoors && (
            <>
              <FieldRow label="How long are you comfortable training indoors?">
                <div className="flex flex-wrap gap-2" data-testid="equipment-indoor-comfort">
                  {[30, 45, 60, 90, 120].map((mins) => (
                    <button
                      key={mins}
                      type="button"
                      data-testid={`equipment-indoor-comfort-${mins}`}
                      data-selected={intake.equipment.indoor_comfort_minutes === mins ? "true" : "false"}
                      onClick={() => patch("equipment", { indoor_comfort_minutes: mins })}
                      className={`rounded-md border px-3 py-2 text-sm ${
                        intake.equipment.indoor_comfort_minutes === mins
                          ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                          : "border-dark-border text-dark-text-primary"
                      }`}
                    >
                      {mins < 60 ? `${mins} min` : `${mins / 60} h`}
                    </button>
                  ))}
                </div>
              </FieldRow>
              <FieldRow label="Anything about indoor training the coach should know?">
                <Textarea
                  data-testid="equipment-indoor-notes"
                  value={intake.equipment.indoor_notes || ""}
                  onChange={(e) => patch("equipment", { indoor_notes: e.target.value || null })}
                  rows={2}
                  placeholder="Example: I can do hard intervals indoors, but dislike indoor sessions longer than 75 minutes."
                />
              </FieldRow>
            </>
          )}
        </div>
      )}
      {isRunningSport(sport) && (
        <div className="space-y-3" data-testid="equipment-running-block">
          <FieldRow label="Do you use a GPS watch?">
            <YesNoUnsure
              value={intake.equipment.has_gps_watch}
              onChange={(v) => patch("equipment", { has_gps_watch: v })}
              testId="equipment-gps"
            />
          </FieldRow>
          <FieldRow label="Do you have access to a treadmill?">
            <YesNoUnsure
              value={intake.equipment.has_treadmill}
              onChange={(v) => patch("equipment", { has_treadmill: v })}
              testId="equipment-treadmill"
            />
          </FieldRow>
        </div>
      )}
      {isSwimmingSport(sport) && (
        <div className="space-y-3" data-testid="equipment-swimming-block">
          <FieldRow label="Do you have pool access?">
            <YesNoUnsure
              value={intake.equipment.has_pool_access}
              onChange={(v) => patch("equipment", { has_pool_access: v })}
              testId="equipment-pool"
            />
          </FieldRow>
          <FieldRow label="Do you have open-water access?">
            <YesNoUnsure
              value={intake.equipment.has_open_water_access}
              onChange={(v) => patch("equipment", { has_open_water_access: v })}
              testId="equipment-open-water"
            />
          </FieldRow>
        </div>
      )}
      <FieldRow label="Anything about your training environment? (optional)">
        <Textarea
          data-testid="equipment-environment-notes"
          value={intake.equipment.environment_notes || ""}
          onChange={(e) => patch("equipment", { environment_notes: e.target.value || null })}
          rows={2}
          placeholder="Example: mostly flat roads, no long climbs nearby, very hot climate."
        />
      </FieldRow>
    </div>
  );
};

const PreferencesStep = ({ intake, patch }) => (
  <div className="space-y-4" data-testid="step-preferences">
    <FieldRow label="What types of workouts do you enjoy?">
      <Textarea
        data-testid="preferences-enjoyed"
        value={intake.training_preferences.enjoyed_workouts || ""}
        onChange={(e) => patch("training_preferences", { enjoyed_workouts: e.target.value || null })}
        rows={2}
        placeholder="Example: endurance rides, tempo, sweet spot, threshold, climbing."
      />
    </FieldRow>
    <FieldRow label="Are there workouts you dislike or prefer to avoid?">
      <Textarea
        data-testid="preferences-disliked"
        value={intake.training_preferences.disliked_workouts || ""}
        onChange={(e) => patch("training_preferences", { disliked_workouts: e.target.value || null })}
        rows={2}
        placeholder="Example: very short sprint sessions or long indoor rides."
      />
    </FieldRow>
    <FieldRow label="How many hard sessions per week feel manageable?">
      <div className="flex flex-wrap gap-2" data-testid="preferences-hard-sessions">
        {[
          { id: "1", label: "1" },
          { id: "2", label: "2" },
          { id: "3", label: "3" },
          { id: "let_endurava_decide", label: "Not sure — let Endurava decide" },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            data-testid={`preferences-hard-sessions-${opt.id}`}
            data-selected={intake.training_preferences.hard_sessions_per_week === opt.id ? "true" : "false"}
            onClick={() => patch("training_preferences", { hard_sessions_per_week: opt.id })}
            className={`rounded-md border px-3 py-2 text-sm ${
              intake.training_preferences.hard_sessions_per_week === opt.id
                ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                : "border-dark-border text-dark-text-primary"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </FieldRow>
    <FieldRow label="Do you prefer:">
      <div className="flex flex-wrap gap-2" data-testid="preferences-session-length">
        {[
          { id: "more_shorter", label: "More shorter sessions" },
          { id: "fewer_longer", label: "Fewer longer sessions" },
          { id: "balanced", label: "Balanced" },
          { id: "let_endurava_decide", label: "Let Endurava decide" },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            data-testid={`preferences-session-length-${opt.id}`}
            data-selected={intake.training_preferences.session_length_preference === opt.id ? "true" : "false"}
            onClick={() =>
              patch("training_preferences", { session_length_preference: opt.id })
            }
            className={`rounded-md border px-3 py-2 text-sm ${
              intake.training_preferences.session_length_preference === opt.id
                ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                : "border-dark-border text-dark-text-primary"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </FieldRow>
    <FieldRow label="Preferred usual session duration (optional)">
      <div className="flex items-center gap-2" data-testid="preferences-preferred-duration">
        <Input
          type="number"
          min="5"
          max="600"
          className="w-24"
          placeholder="75"
          data-testid="preferences-preferred-duration-input"
          value={
            intake.training_preferences.preferred_session_duration_minutes ?? ""
          }
          onChange={(e) =>
            patch("training_preferences", {
              preferred_session_duration_minutes:
                e.target.value ? Number(e.target.value) : null,
            })
          }
        />
        <span className="text-xs text-dark-text-muted">
          minutes — a usual-session target (e.g. 55, 75, 90). Each day's
          minutes above are still the hard cap; health session limits outrank
          both.
        </span>
      </div>
    </FieldRow>
    <FieldRow label="Anything else about how you like to train? (optional)">
      <Textarea
        data-testid="preferences-notes"
        value={intake.training_preferences.additional_notes || ""}
        onChange={(e) => patch("training_preferences", { additional_notes: e.target.value || null })}
        rows={2}
      />
    </FieldRow>
  </div>
);

const StrengthStep = ({ intake, patch }) => (
  <div className="space-y-4" data-testid="step-strength">
    <FieldRow label="Would you like strength training included?">
      <YesNoUnsure
        value={intake.strength.include_strength}
        onChange={(v) => patch("strength", { include_strength: v })}
        testId="strength-include"
        options={[
          { id: "yes", label: "Yes" },
          { id: "no", label: "No" },
          { id: "let_endurava_decide", label: "Let Endurava decide" },
        ]}
      />
    </FieldRow>
    {intake.strength.include_strength === "yes" && (
      <div className="space-y-3" data-testid="strength-details">
        <FieldRow label="Days per week (optional)">
          <Input
            type="number"
            min="0"
            max="7"
            data-testid="strength-days"
            value={intake.strength.strength_days_per_week || ""}
            onChange={(e) =>
              patch("strength", {
                strength_days_per_week: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </FieldRow>
        <FieldRow label="Gym access?">
          <YesNoUnsure
            value={intake.strength.gym_access}
            onChange={(v) => patch("strength", { gym_access: v })}
            testId="strength-gym"
          />
        </FieldRow>
        <FieldRow label="Any notes about strength (optional)">
          <Textarea
            data-testid="strength-notes"
            value={intake.strength.strength_notes || ""}
            onChange={(e) => patch("strength", { strength_notes: e.target.value || null })}
            rows={2}
          />
        </FieldRow>
      </div>
    )}
    <FieldRow label="Do you regularly do another sport or activity that should count toward your training load?">
      <YesNoUnsure
        value={intake.strength.other_sport_activity}
        onChange={(v) => patch("strength", { other_sport_activity: v })}
        testId="strength-other-sport"
      />
    </FieldRow>
    {intake.strength.other_sport_activity && (
      <FieldRow label="Describe the activity (optional)">
        <Textarea
          data-testid="strength-other-notes"
          value={intake.strength.other_sport_notes || ""}
          onChange={(e) => patch("strength", { other_sport_notes: e.target.value || null })}
          rows={2}
          placeholder="Example: Football every Wednesday for 90 minutes."
        />
      </FieldRow>
    )}
  </div>
);

const NutritionStep = ({ intake, patch }) => (
  <div className="space-y-4" data-testid="step-nutrition">
    <FieldRow label="Would you like nutrition and fueling guidance included with your training plan?">
      <YesNoUnsure
        value={intake.nutrition.nutrition_opt_in}
        onChange={(v) => patch("nutrition", { nutrition_opt_in: v })}
        testId="nutrition-opt-in"
        options={[
          { id: "yes", label: "Yes" },
          { id: "no", label: "No" },
        ]}
      />
    </FieldRow>
    {intake.nutrition.nutrition_opt_in === "yes" && (
      <div className="space-y-3" data-testid="nutrition-details">
        <FieldRow label="Primary nutrition goal (optional)">
          <Textarea
            data-testid="nutrition-goal"
            value={intake.nutrition.primary_nutrition_goal || ""}
            onChange={(e) =>
              patch("nutrition", { primary_nutrition_goal: e.target.value || null })
            }
            rows={2}
            placeholder="Example: lose 3 kg without losing FTP."
          />
        </FieldRow>
        <FieldRow label="Current body weight (kg, optional)">
          <PrefillHint
            label={
              intake.nutrition.current_body_weight_kg
                ? `${intake.nutrition.current_body_weight_kg} kg`
                : null
            }
          />
          <Input
            type="number"
            min="0"
            data-testid="nutrition-weight"
            value={intake.nutrition.current_body_weight_kg || ""}
            onChange={(e) =>
              patch("nutrition", {
                current_body_weight_kg: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </FieldRow>
        <FieldRow label="Dietary restrictions / allergies / foods you avoid (optional)">
          <Textarea
            data-testid="nutrition-restrictions"
            value={intake.nutrition.dietary_restrictions || ""}
            onChange={(e) =>
              patch("nutrition", { dietary_restrictions: e.target.value || null })
            }
            rows={2}
            placeholder="Example: vegetarian, lactose intolerant, do not tolerate gels well."
          />
        </FieldRow>
        <FieldRow label="Long-session fueling habits (optional)">
          <Textarea
            data-testid="nutrition-fueling"
            value={intake.nutrition.long_session_fueling_habits || ""}
            onChange={(e) =>
              patch("nutrition", { long_session_fueling_habits: e.target.value || null })
            }
            rows={2}
          />
        </FieldRow>
        <FieldRow label="Approximate carbs per hour on long sessions (g/hr, optional)">
          <Input
            type="number"
            min="0"
            data-testid="nutrition-carbs"
            value={intake.nutrition.carbs_per_hour_grams || ""}
            onChange={(e) =>
              patch("nutrition", {
                carbs_per_hour_grams: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </FieldRow>
        <FieldRow label="Attach workout-specific fueling guidance to sessions?">
          <YesNoUnsure
            value={intake.nutrition.workout_specific_fueling_desired}
            onChange={(v) =>
              patch("nutrition", { workout_specific_fueling_desired: v })
            }
            testId="nutrition-per-session"
          />
        </FieldRow>
      </div>
    )}
  </div>
);

const ReviewRow = ({ label, value, onEdit, testId }) => (
  <div className="flex items-start justify-between gap-4 border-b border-dark-border py-2" data-testid={testId}>
    <div>
      <p className="text-xs uppercase tracking-widest text-dark-text-muted">{label}</p>
      <p className="text-sm text-dark-text-primary">{value || "—"}</p>
    </div>
    <Button type="button" variant="ghost" size="sm" onClick={onEdit} data-testid={`${testId}-edit`}>
      Edit
    </Button>
  </div>
);

const summariseAvailability = (avail) => {
  const active = (avail.days || []).filter((d) => d.available);
  if (active.length === 0) return "No days selected";
  const perDay = active
    .map((d) => `${DAY_LABEL[d.weekday] || d.weekday}${d.max_minutes ? ` (${d.max_minutes}m)` : ""}`)
    .join(", ");
  const totalMinutes = active.reduce(
    (sum, d) => sum + (Number.isFinite(d.max_minutes) ? d.max_minutes : 0),
    0,
  );
  if (totalMinutes <= 0) return perDay;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const totalLabel = hours > 0 ? `${hours}h${mins ? ` ${mins}m` : ""}` : `${mins}m`;
  return `${perDay} · ~${totalLabel}/week`;
};

const formatEventSummary = (event) => {
  if (!event?.preparing_for_event && !event?.event_name && !event?.event_date) {
    return "None";
  }
  const parts = [event.event_name || "Event"];
  if (event.event_date) {
    try {
      const d = new Date(`${event.event_date}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        parts.push(
          `on ${d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}`,
        );
      } else {
        parts.push(`on ${event.event_date}`);
      }
    } catch {
      parts.push(`on ${event.event_date}`);
    }
  }
  if (event.target_distance_meters) {
    parts.push(`${(event.target_distance_meters / 1000).toFixed(0)} km`);
  }
  return parts.join(" ");
};

const ReviewStep = ({ intake, jumpToStep, profileOnly = false }) => {
  const primaryCf =
    (intake.current_fitness || []).find((r) => r.sport === intake.primary_sport) || {};
  const goalLabel = (GOAL_EXAMPLES[intake.primary_sport] || []).find(
    (g) => g.id === intake.goal.goal_category,
  )?.label || intake.goal.goal_category;
  return (
    <div className="space-y-1" data-testid="step-review">
      <ReviewRow label="Sport" value={intake.primary_sport} onEdit={() => jumpToStep(1)} testId="review-sport" />
      {!profileOnly && (
        <>
          <ReviewRow label="Goal" value={goalLabel} onEdit={() => jumpToStep(1)} testId="review-goal" />
          {goalNeedsEvent(intake.goal.goal_category) && (
            <ReviewRow
              label="Event"
              value={formatEventSummary(intake.event)}
              onEdit={() => jumpToStep(1)}
              testId="review-event"
            />
          )}
        </>
      )}
      <ReviewRow
        label="Sport background"
        value={
          primaryCf.experience_level
            ? primaryCf.experience_level.charAt(0).toUpperCase() + primaryCf.experience_level.slice(1)
            : "—"
        }
        onEdit={() => jumpToStep(1)}
        testId="review-current-fitness"
      />
      {!profileOnly && (
        <ReviewRow
          label="Weekly availability"
          value={summariseAvailability(intake.availability)}
          onEdit={() => jumpToStep(3)}
          testId="review-availability"
        />
      )}
      <ReviewRow
        label="Health / limitations"
        value={
          intake.medical.disclosure === "yes"
            ? intake.medical.description || "Yes (details provided)"
            : intake.medical.disclosure === "prefer_not_to_say"
              ? "Prefer not to say"
              : "None"
        }
        onEdit={() => jumpToStep(2)}
        testId="review-medical"
      />
      <ReviewRow
        label="Strength / cross-training"
        value={intake.strength.include_strength || "—"}
        onEdit={() => jumpToStep(2)}
        testId="review-strength"
      />
      <ReviewRow
        label="Training preferences"
        value={
          intake.training_preferences.enjoyed_workouts
            ? intake.training_preferences.enjoyed_workouts.slice(0, 80)
            : "—"
        }
        onEdit={() => jumpToStep(3)}
        testId="review-preferences"
      />
      <ReviewRow
        label="Nutrition"
        value={intake.nutrition.nutrition_opt_in === "yes" ? "Included" : "Not requested"}
        onEdit={() => jumpToStep(3)}
        testId="review-nutrition"
      />
      {!profileOnly && (
        <ReviewRow
          label="Readiness"
          value={READINESS_FIELDS.map((f) => intake.readiness[f.key]).filter(Boolean).join(" / ") || "Skipped"}
          onEdit={() => jumpToStep(3)}
          testId="review-readiness"
        />
      )}
    </div>
  );
};

export default TrainingPlanSetup;
